import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

export default function Home() {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <div className="relative bg-[#050505]">
      <div className="stark-bg" />
      
      {/* 1. HERO — The big promise */}
      <section className="min-h-screen flex flex-col justify-center px-8 max-w-[1600px] mx-auto pt-32 pb-20">
        <motion.div {...fadeUp}>
          <h1 className="text-6xl md:text-[9rem] font-black leading-[0.85] tracking-tighter uppercase mb-12">
            YOUR IDEA.<br />
            <span className="text-transparent border-text stroke-white" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.3)' }}>BUILT AT</span><br />
            <span className="text-stark-lime">STARK SPEED.</span>
          </h1>
          
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-medium mb-12">
                Build powerful custom applications for your business in a fraction of the time of traditional software development.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 mb-12">
                <Link 
                  to="/start" 
                  className="group relative px-10 py-6 bg-stark-lime text-black font-black text-xl rounded-full overflow-hidden transition-all duration-500 hover:scale-105 text-center"
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    BUILD MY APPLICATION <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
                <a 
                  href="#work" 
                  className="group px-10 py-6 glass-card text-white font-black text-xl rounded-full transition-all duration-500 hover:bg-white/10 text-center flex items-center justify-center gap-3"
                >
                  SEE WHAT WE'VE BUILT <SafeIcon icon={FiIcons.FiArrowDown} className="group-hover:translate-y-1 transition-transform" />
                </a>
              </div>

              <div className="inline-flex items-center gap-4 py-3 px-6 glass-card rounded-full border-white/5">
                <div className="w-2 h-2 rounded-full bg-stark-pink animate-pulse" />
                <span className="text-sm font-black tracking-widest uppercase text-white/60">
                  From business problem → working application → live product.
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 2. THE OLD WAY VS THE STARK WAY */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5">
        <motion.div {...fadeUp} className="grid lg:grid-cols-2 gap-12">
          {/* Traditional */}
          <div className="glass-card p-12 md:p-16 rounded-[3rem] opacity-50 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-700">
            <h3 className="text-2xl font-black text-white/40 mb-12 tracking-widest uppercase">Traditional Software</h3>
            <div className="space-y-6 text-xl font-bold text-white/30">
              <div className="flex flex-col items-center gap-4">
                <span>Idea</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Requirements</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Design</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Development</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Testing</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Revisions</span>
                <SafeIcon icon={FiIcons.FiArrowDown} />
                <span>Deployment</span>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-white/10 text-center">
              <p className="text-red-400 font-bold uppercase tracking-widest">Months of development. Large teams. Large budgets.</p>
            </div>
          </div>

          {/* The Stark Way */}
          <div className="bg-stark-lime/5 border border-stark-lime/20 p-12 md:p-16 rounded-[3rem] relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-stark-lime/10 blur-[100px] rounded-full group-hover:bg-stark-lime/20 transition-colors duration-1000" />
            <h3 className="text-2xl font-black text-stark-lime mb-12 tracking-widest uppercase relative z-10">The Stark Way</h3>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-2xl md:text-4xl font-black text-white relative z-10 py-12">
              <span className="text-white">IDEA</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="text-stark-lime" />
              <span className="text-white">BUILD</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="text-stark-lime" />
              <span className="text-white">TEST</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="text-stark-lime" />
              <span className="text-white">IMPROVE</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="text-stark-pink" />
              <span className="text-stark-pink">LIVE</span>
            </div>
            <div className="mt-12 pt-8 border-t border-stark-lime/20 text-center relative z-10">
              <p className="text-stark-lime font-bold uppercase tracking-widest">Less bureaucracy. Faster iterations. Working software sooner.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 3. WHAT CAN STARK BUILD? */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto">
        <motion.div {...fadeUp} className="mb-20">
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">What can Stark build?</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {[
            { title: 'Business Platforms', desc: 'Custom systems built around how your business actually operates.' },
            { title: 'Internal Applications', desc: 'Replace spreadsheets, emails and fragmented workflows.' },
            { title: 'Customer Applications', desc: 'Build powerful digital experiences for your customers.' },
            { title: 'Automation', desc: 'Turn repetitive processes into automated workflows.' },
            { title: 'Dashboards', desc: 'Bring your business data into one intelligent interface.' },
            { title: 'Platforms & Marketplaces', desc: 'Build entirely new digital products and revenue streams.' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card p-10 rounded-[2rem] hover:bg-white/10 transition-colors"
            >
              <h3 className="text-2xl font-black mb-4">{item.title}</h3>
              <p className="text-white/50 font-medium leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp} className="flex justify-center">
          <Link to="/start" className="inline-flex items-center gap-3 text-2xl font-black text-stark-pink hover:text-white transition-colors uppercase tracking-tight">
            I HAVE AN IDEA <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
        </motion.div>
      </section>

      {/* 4. SHOWCASE */}
      <section id="work" className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5">
        <motion.div {...fadeUp} className="mb-24">
          <h2 className="text-6xl md:text-8xl font-black uppercase tracking-tighter mb-6 leading-none">
            WE DON'T JUST TALK<br/>ABOUT BUILDING.<br/>
            <span className="text-stark-pink">WE BUILD.</span>
          </h2>
          <p className="text-2xl text-white/40 font-medium">Explore applications we've already built for real businesses.</p>
        </motion.div>

        <div className="space-y-40">
          {/* Showcase 1: SalvageOS */}
          <motion.div {...fadeUp} className="group">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <h3 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">SALVAGEOS</h3>
                <p className="text-stark-lime font-black tracking-widest uppercase text-sm mb-6">Insurance salvage management platform</p>
                <p className="text-2xl text-white/60 font-medium leading-relaxed mb-12">
                  Automates salvage distribution, bidding, categorisation and reporting. Replaced 5 disparate legacy systems with one Stark-built interface.
                </p>
                <Link to="/start" className="inline-flex items-center gap-3 font-black text-white hover:text-stark-pink transition-colors uppercase tracking-tight border-b-2 border-white/20 hover:border-stark-pink pb-1">
                  EXPLORE PROJECT <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="relative aspect-[4/3] rounded-[2rem] bg-[#111] border border-white/10 overflow-hidden flex items-center justify-center p-8 group-hover:border-stark-lime/30 transition-colors duration-500">
                  {/* Mock UI */}
                  <div className="w-full h-full glass-card rounded-xl flex flex-col overflow-hidden shadow-2xl">
                    <div className="h-12 border-b border-white/10 flex items-center px-6 gap-4 bg-white/5">
                      <div className="w-3 h-3 rounded-full bg-red-500/50" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                      <div className="w-3 h-3 rounded-full bg-green-500/50" />
                      <div className="ml-4 h-4 w-32 bg-white/10 rounded-full" />
                    </div>
                    <div className="flex-1 p-8 flex gap-6">
                      <div className="w-1/4 space-y-4">
                        <div className="h-8 bg-stark-lime/20 rounded-lg w-full" />
                        <div className="h-8 bg-white/5 rounded-lg w-full" />
                        <div className="h-8 bg-white/5 rounded-lg w-full" />
                      </div>
                      <div className="flex-1 space-y-6">
                        <div className="h-32 bg-white/5 rounded-2xl w-full" />
                        <div className="grid grid-cols-2 gap-6">
                          <div className="h-40 bg-white/5 rounded-2xl" />
                          <div className="h-40 bg-white/5 rounded-2xl" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Showcase 2: InsideOut */}
          <motion.div {...fadeUp} className="group">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="relative aspect-[4/3] rounded-[2rem] bg-[#111] border border-white/10 overflow-hidden flex items-center justify-center p-8 group-hover:border-stark-pink/30 transition-colors duration-500">
                  <div className="w-[40%] h-[80%] glass-card rounded-[2rem] flex flex-col overflow-hidden shadow-2xl border-4 border-[#222]">
                    <div className="h-16 flex items-center justify-center border-b border-white/10 bg-white/5">
                      <div className="w-1/2 h-2 bg-white/20 rounded-full" />
                    </div>
                    <div className="flex-1 p-6 space-y-6">
                      <div className="w-full h-32 bg-stark-pink/20 rounded-xl" />
                      <div className="w-3/4 h-6 bg-white/10 rounded-full" />
                      <div className="w-1/2 h-6 bg-white/10 rounded-full" />
                      <div className="grid grid-cols-2 gap-4 mt-auto">
                        <div className="h-12 bg-white/5 rounded-xl" />
                        <div className="h-12 bg-stark-pink/80 rounded-xl" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-5">
                <h3 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">INSIDEOUT</h3>
                <p className="text-stark-pink font-black tracking-widest uppercase text-sm mb-6">Customer-Facing Application</p>
                <p className="text-2xl text-white/60 font-medium leading-relaxed mb-12">
                  A seamless mobile-first experience connecting users with wellness resources instantly. From concept to App Store in 4 weeks.
                </p>
                <Link to="/start" className="inline-flex items-center gap-3 font-black text-white hover:text-stark-pink transition-colors uppercase tracking-tight border-b-2 border-white/20 hover:border-stark-pink pb-1">
                  EXPLORE PROJECT <SafeIcon icon={FiIcons.FiArrowRight} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5. IMAGINE WHAT WE COULD BUILD */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5 text-center">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-16">
            YOU KNOW THE PROBLEM.<br/>
            <span className="text-stark-lime">WE'LL BUILD THE SOLUTION.</span>
          </h2>
          <div className="space-y-12 mb-20 text-left md:text-center">
            {[
              "Our team still manages this in Excel.",
              "Our customers have to email us to do this.",
              "Three different systems don't talk to each other.",
              "We have an idea for a completely new product."
            ].map((problem, idx) => (
              <div key={idx} className="group">
                <p className="text-2xl md:text-3xl text-white/40 font-medium mb-2 group-hover:text-white transition-colors">"{problem}"</p>
                <p className="text-xl md:text-2xl font-black text-stark-lime uppercase tracking-tight">We can build an application for that.</p>
              </div>
            ))}
          </div>
          <Link 
            to="/start" 
            className="inline-block px-12 py-6 bg-white text-black font-black text-2xl rounded-full hover:bg-stark-lime transition-all duration-300 uppercase tracking-tighter hover:scale-105"
          >
            WHAT DO YOU WANT TO BUILD? →
          </Link>
        </motion.div>
      </section>

      {/* 6. HOW STARK WORKS */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5">
        <motion.div {...fadeUp} className="mb-20">
          <h2 className="text-5xl font-black uppercase tracking-tighter">How Stark Works</h2>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6 mb-24">
          {[
            { num: '01', title: 'DISCOVER', desc: 'We understand your business, users and problem.' },
            { num: '02', title: 'BUILD', desc: 'We rapidly turn the concept into a working application.' },
            { num: '03', title: 'TEST', desc: 'Your team uses it, gives feedback and identifies what needs changing.' },
            { num: '04', title: 'SCALE', desc: 'We refine, deploy and continue developing your application.' }
          ].map((step, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card p-10 rounded-[2rem]"
            >
              <span className="text-stark-pink font-black text-xl block mb-6">{step.num} — {step.title}</span>
              <p className="text-white/60 font-medium text-lg">{step.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto">
          <p className="text-3xl md:text-5xl font-black uppercase tracking-tighter leading-tight">
            You don't wait months to see what you're getting.<br/>
            <span className="text-stark-lime">You see it. Use it. Improve it.</span>
          </p>
        </motion.div>
      </section>

      {/* 7. WHY STARK? */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5">
        <motion.div {...fadeUp} className="mb-20">
          <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">Why Stark?</h2>
          <p className="text-2xl text-white/40 font-medium">"Why wouldn't I just use a traditional software company?"</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {[
            { title: 'SPEED', desc: 'AI-native development lets us move from concept to working software dramatically faster.' },
            { title: 'ITERATION', desc: "Don't spend months writing specifications for something you've never seen. Build it, test it and improve it." },
            { title: 'BUSINESS FIRST', desc: "We don't start with technology. We start with your business problem." },
            { title: 'LOWER DEVELOPMENT OVERHEAD', desc: 'A lean, AI-powered development model means more of your budget goes into the product — not layers of traditional development overhead.' }
          ].map((point, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="border-l-4 border-stark-pink pl-8 py-2"
            >
              <h3 className="text-3xl font-black uppercase tracking-tight mb-4">{point.title}</h3>
              <p className="text-xl text-white/60 font-medium leading-relaxed">{point.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. THE "FROM IDEA TO APPLICATION" VISUAL */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5 overflow-hidden">
        <div className="max-w-5xl mx-auto glass-card p-8 md:p-16 rounded-[3rem] text-center">
          <AnimatedProcessSequence />
        </div>
      </section>

      {/* 9. WHO IS STARK FOR? */}
      <section className="py-32 px-8 max-w-[1600px] mx-auto border-t border-white/5">
        <motion.div {...fadeUp} className="mb-20">
          <h2 className="text-5xl font-black uppercase tracking-tighter mb-4">Who is Stark for?</h2>
          <p className="text-2xl text-stark-lime font-black uppercase tracking-widest">For companies that need to move.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { target: 'ENTERPRISE', desc: 'Modernise legacy processes and build new internal systems.' },
            { target: 'SMEs', desc: 'Build software that previously would have been too expensive or time-consuming.' },
            { target: 'STARTUPS', desc: 'Turn an idea into a working product without spending a year in development.' },
            { target: 'INNOVATION TEAMS', desc: 'Prototype, test and launch new ideas at unprecedented speed.' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card p-10 rounded-[2rem] border-t border-white/10 hover:border-t-stark-pink transition-colors"
            >
              <h3 className="text-2xl font-black mb-4 uppercase tracking-tighter">{item.target}</h3>
              <p className="text-white/50 font-medium">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 10. THE FUNNEL (Final CTA) */}
      <section className="py-40 px-8 max-w-[1600px] mx-auto border-t border-white/5 text-center flex flex-col items-center">
        <motion.div {...fadeUp}>
          <h2 className="text-[8vw] md:text-[6rem] font-black tracking-tighter uppercase leading-none mb-8">
            SO, WHAT DO YOU<br/>
            <span className="text-stark-pink">WANT TO BUILD?</span>
          </h2>
          <p className="text-2xl text-white/60 font-medium max-w-3xl mx-auto mb-16">
            Tell Stark what you're trying to accomplish. We'll help you figure out what to build.
          </p>
          <Link 
            to="/start" 
            className="inline-flex px-16 py-8 bg-stark-lime text-black font-black text-3xl md:text-4xl rounded-full hover:bg-white transition-all duration-300 uppercase tracking-tighter hover:scale-105 items-center gap-4"
          >
            START MY PROJECT <SafeIcon icon={FiIcons.FiArrowRight} />
          </Link>
        </motion.div>
        
        <div className="mt-40 w-full flex justify-between text-white/30 text-xs font-black tracking-widest uppercase">
          <span>© STARK {new Date().getFullYear()}</span>
          <span>AI-NATIVE APPLICATION STUDIO</span>
        </div>
      </section>
    </div>
  );
}

// Component for Section 8 animation
function AnimatedProcessSequence() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="h-[400px] flex flex-col items-center justify-center relative">
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div 
            key="idea"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center"
          >
            <span className="text-stark-lime font-black tracking-widest uppercase text-sm mb-6 block">Your Idea</span>
            <div className="glass-card p-6 rounded-2xl inline-block">
              <p className="text-2xl font-bold font-mono">"We need an application to manage our insurance salvage."</p>
            </div>
          </motion.div>
        )}
        
        {step === 1 && (
          <motion.div 
            key="stark"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            className="text-center"
          >
            <span className="text-white/40 font-black tracking-widest uppercase text-sm mb-6 block">STARK AI</span>
            <div className="flex flex-col gap-4 text-3xl font-black uppercase text-stark-pink">
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>Analyses</motion.span>
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>Designs</motion.span>
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>Builds</motion.span>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div 
            key="app"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="text-center w-full max-w-2xl"
          >
            <span className="text-white font-black tracking-widest uppercase text-sm mb-6 block">Your Application</span>
            <div className="w-full h-64 bg-[#111] border border-stark-lime/30 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(212,240,122,0.1)] p-4 flex flex-col">
              <div className="h-8 border-b border-white/10 flex justify-between items-center mb-4">
                <div className="w-24 h-3 bg-white/20 rounded" />
                <div className="w-8 h-8 bg-stark-lime rounded-full" />
              </div>
              <div className="flex-1 flex gap-4">
                <div className="w-1/3 bg-white/5 rounded-lg" />
                <div className="w-2/3 flex flex-col gap-4">
                  <div className="h-1/2 bg-stark-pink/20 rounded-lg" />
                  <div className="h-1/2 bg-white/5 rounded-lg" />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div 
            key="loop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center"
          >
            <h3 className="text-4xl font-black uppercase tracking-tighter mb-4">Ready to vibe code?</h3>
            <div className="w-12 h-12 border-t-4 border-stark-pink rounded-full animate-spin mx-auto" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}